import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HardDrive, RefreshCw, Plus, MapPin, AlertCircle, Radio, CheckCircle2,
  X, Save, Loader2, LayoutGrid, List, Pencil, Trash2, Eye, Server,
  Wifi, WifiOff, Clock, Users, Download, Play, Database, Upload, UserPlus, Activity, Search, Building2
} from 'lucide-react';
import { deviceService, locationService, departmentService, employeeService } from '../services';
import siteService from '../services/siteService';
import zktecoService, { SyncResult, SyncAllResult, SyncEmployeesResult, FetchLogsResult } from '../services/zktecoService';
import api from '../services/api';
import signalRService, { SyncProgressMessage } from '../services/signalrService';
import type { Device, DeviceStats, PingResult, DeviceCreateDto, DeviceUpdateDto, LocationDropdownItem, Department } from '../services';
import { uyar, hata, onayla } from '../components/GlobalModal';

type ViewMode = 'card' | 'list';
type ModalMode = 'create' | 'edit' | 'view' | null;

const DEVICE_TYPES = [
  { value: 'ZKTeco', label: 'ZKTeco' },
  { value: 'Anviz', label: 'Anviz' },
  { value: 'Hikvision', label: 'Hikvision' },
  { value: 'Dahua', label: 'Dahua' },
  { value: 'Other', label: 'Other' }
];

const PROTOCOLS = [
  { value: 'ZKTeco', label: 'ZKTeco', defaultPort: 4370 },
  { value: 'Optex', label: 'Optex T-10', defaultPort: 5005 },
  { value: 'Timmy', label: 'Timmy (Anviz)', defaultPort: 5010 },
  { value: 'Anviz', label: 'Anviz', defaultPort: 5010 },
  { value: 'Proxen', label: 'Proxen SS900', defaultPort: 5010 },
  { value: 'Hikvision', label: 'Hikvision (HTTP Push)', defaultPort: 80 }
];

const initialFormData = {
  deviceName: '',
  deviceCode: '',
  ipAddress: '',
  port: 4370,
  locationId: null as number | null,
  location: '',
  deviceType: 'ZKTeco',
  protocol: 'ZKTeco',
  serialNumber: '',
  model: '',
  macAddress: '',
  isActive: true
};

// IP Address validation (IPv4)
const isValidIpAddress = (ip: string): boolean => {
  if (!ip) return false;
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipRegex.test(ip);
};

// MAC Address validation
const isValidMacAddress = (mac: string): boolean => {
  if (!mac) return true; // MAC is optional
  // Accept formats: 00:1A:2B:3C:4D:5E, 00-1A-2B-3C-4D-5E, 001A2B3C4D5E
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$|^([0-9A-Fa-f]{12})$/;
  return macRegex.test(mac);
};

// Format MAC Address to standard format
const formatMacAddress = (mac: string): string => {
  if (!mac) return '';
  // Remove any existing separators and convert to uppercase
  const cleaned = mac.replace(/[:-]/g, '').toUpperCase();
  if (cleaned.length !== 12) return mac;
  // Format as XX:XX:XX:XX:XX:XX
  return cleaned.match(/.{2}/g)?.join(':') || mac;
};

// 2026-08-15: Cihaz Hikvision mi? Protocol veya DeviceType alanindan anlasilir.
// ZKTeco'ya ozel islemler bu cihazlarda gizlenir (kullanici hata yapmasin).
const isHikvisionCihaz = (d: any): boolean =>
  (d?.protocol || d?.deviceType || '').toLowerCase().includes('hikvision');

const Devices = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [locations, setLocations] = useState<LocationDropdownItem[]>([]);
  const [pingingId, setPingingId] = useState<number | null>(null);
  const [pingResults, setPingResults] = useState<Record<number, PingResult>>({});
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Ag 2026: Hikvision Veri Cek tarih araligi modal
  const [hikPullingId, setHikPullingId] = useState<number | null>(null);
  const [hikPullModal, setHikPullModal] = useState<{ device: any; startDate: string; endDate: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Device | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Sync states
  const [syncingDeviceId, setSyncingDeviceId] = useState<number | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncResult, setSyncResult] = useState<{ type: 'single' | 'all'; data: any } | null>(null);

  // Bulk sync states
  const [bulkSyncModal, setBulkSyncModal] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkSyncProgress, setBulkSyncProgress] = useState({ current: 0, total: 0, deviceName: '', message: '' });
  const [bulkSyncResult, setBulkSyncResult] = useState<{
    totalDevices: number; successCount: number; failedCount: number;
    totalLogs: number; totalSaved: number; results: any[];
  } | null>(null);

  // Upload/Download modal states
  const [uploadModal, setUploadModal] = useState<Device | null>(null);
  const [downloadModal, setDownloadModal] = useState<Device | null>(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]); // Coklu cihaz secimi
  const [bulkUploadModal, setBulkUploadModal] = useState(false); // Toplu yukleme modali
  const [departments, setDepartments] = useState<Department[]>([]);
  // 2026-08-15 (kullanici: "santiye, lokasyon secimi ve tek tek / toplu
  // checkbox secimi vardi"): Cihaza personel gonderme modali artik puantaj
  // ekranindaki filtre setinin aynisini kullaniyor ve gonderilecek kisiler
  // listeden tek tek isaretlenebiliyor.
  const [uploadForm, setUploadForm] = useState({
    departmentId: null as number | null,
    siteId: null as number | null,
    locationId: null as number | null,
    includeInactive: false,
    clearFirst: false,
    dateFilter: 'all' as 'all' | 'today' | 'week'
  });
  const [sites, setSites] = useState<{ id: number; name: string }[]>([]);
  const [uploadEmployees, setUploadEmployees] = useState<any[]>([]);
  const [uploadEmpLoading, setUploadEmpLoading] = useState(false);
  const [uploadSearch, setUploadSearch] = useState('');
  const [selectedUploadEmpIds, setSelectedUploadEmpIds] = useState<number[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [downloadForm, setDownloadForm] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [uploadResult, setUploadResult] = useState<SyncEmployeesResult | null>(null);
  const [downloadResult, setDownloadResult] = useState<FetchLogsResult | null>(null);

  // Preview modal states (cihazdaki personel listesi onizleme)
  const [previewModal, setPreviewModal] = useState<Device | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUsers, setPreviewUsers] = useState<{ enrollNumber: string; name: string; cardNumber: string }[]>([]);
  const [selectedPreviewUsers, setSelectedPreviewUsers] = useState<string[]>([]);
  const [deletingUsers, setDeletingUsers] = useState(false);
  const [editingCard, setEditingCard] = useState<{ enrollNumber: string; cardNumber: string } | null>(null);
  const [savingCard, setSavingCard] = useState(false);

  // Teshis modal
  const [diagnoseModal, setDiagnoseModal] = useState<Device | null>(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseResults, setDiagnoseResults] = useState<string[]>([]);
  const [diagnoseSuccess, setDiagnoseSuccess] = useState<boolean | null>(null);

  // Import users from device state
  const [importingUsers, setImportingUsers] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    totalOnDevice?: number;
    imported?: number;
    skipped?: number;
    importedUsers?: string[];
    errors?: string[];
  } | null>(null);

  useEffect(() => {
    loadData();
    loadDepartments();
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const res = await siteService.getDropdown();
      if (res.success && Array.isArray(res.data)) {
        setSites(res.data.map((x: any) => ({ id: x.id, name: x.name })));
      }
    } catch (err) {
      console.error('Sites load error:', err);
    }
  };

  // Personel gonderme modali acildiginda listeyi getir.
  // Filtreleme FRONTEND'de yapiliyor ki kullanici kimin gidecegini
  // gondermeden ONCE gorsun; "Filtreli Gonder" ise ayni filtreleri
  // backend'e yollar (5000+ personelde tek tek id gondermemek icin).
  useEffect(() => {
    if (!uploadModal) return;
    setSelectedUploadEmpIds([]);
    setUploadSearch('');
    setUploadEmpLoading(true);
    employeeService.getAll()
      .then((r: any) => {
        const arr: any[] = r?.data?.items ?? r?.data ?? [];
        setUploadEmployees(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setUploadEmployees([]))
      .finally(() => setUploadEmpLoading(false));
  }, [uploadModal]);

  // Modaldaki filtrelerin uygulanmis hali (ekranda gorunen liste)
  const filtrelenmisYuklemePersoneli = React.useMemo(() => {
    let rows = uploadEmployees;
    if (!uploadForm.includeInactive) rows = rows.filter((e: any) => e.isActive !== false);
    if (uploadForm.departmentId) {
      rows = rows.filter((e: any) => e.departmentId === uploadForm.departmentId);
    }
    if (uploadForm.siteId) {
      rows = rows.filter((e: any) => e.siteId === uploadForm.siteId);
    }
    if (uploadForm.locationId) {
      rows = rows.filter((e: any) => e.locationId === uploadForm.locationId);
    }
    const q = uploadSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((e: any) =>
        (e.fullName || '').toLowerCase().includes(q) ||
        (e.employeeCode || '').toLowerCase().includes(q) ||
        (e.cardNumber || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [uploadEmployees, uploadForm.departmentId, uploadForm.siteId, uploadForm.locationId, uploadForm.includeInactive, uploadSearch]);

  const tumUploadPersoneliSec = () => {
    const hepsi = filtrelenmisYuklemePersoneli.map((e: any) => e.id);
    const hepsiSecili = hepsi.length > 0 && hepsi.every((id: number) => selectedUploadEmpIds.includes(id));
    setSelectedUploadEmpIds(hepsiSecili ? [] : hepsi);
  };

  const uploadPersoneliDegistir = (id: number) => {
    setSelectedUploadEmpIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const loadDepartments = async () => {
    try {
      const res = await departmentService.getAll();
      if (res.success && res.data) {
        // API'den gelen veriyi Department formatına dönüştür
        const mapped = res.data.map((d: any) => ({
          id: d.id || d.departmentId || d.departmentID,
          departmentId: d.departmentId || d.departmentID || d.id,
          name: d.name || d.departmentName,
          departmentName: d.departmentName || d.name
        }));
        setDepartments(mapped);
      }
    } catch (err) {
      console.error('Departments load error:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [devicesRes, statsRes, locationsRes] = await Promise.all([
        deviceService.getAll(),
        deviceService.getStats(),
        locationService.getForDropdown()
      ]);

      if (devicesRes.success) setDevices(devicesRes.data);
      if (statsRes.success) setStats(statsRes.data);
      if (locationsRes.success) {
        console.log('Locations API response:', locationsRes.data);
        setLocations(locationsRes.data);
      } else {
        console.log('Locations API failed:', locationsRes);
      }

    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.message || t('devices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Ag 2026: Hikvision cihazdan tarih araliginda olay cek (backend /api/hikvision/pull-events-by-device/{id})
  const openHikvisionPullModal = (device: Device) => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    setHikPullModal({ device, startDate: fmt(weekAgo), endDate: fmt(today) });
  };
  const executeHikvisionPull = async () => {
    if (!hikPullModal) return;
    const { device, startDate, endDate } = hikPullModal;
    if (!startDate || !endDate) { uyar('Tarih araligi zorunlu'); return; }
    if (new Date(startDate) > new Date(endDate)) { uyar('Baslangic bitisten sonra olamaz'); return; }
    setHikPullingId(device.id);
    setHikPullModal(null);
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const startTime = encodeURIComponent(`${startDate}T00:00:00+03:00`);
      const endTime = encodeURIComponent(`${endDate}T23:59:59+03:00`);
      const url = `${API_BASE}/api/hikvision/pull-events-by-device/${device.id}?startTime=${startTime}&endTime=${endTime}&maxResults=5000`;
      const resp = await fetch(url, { method: 'POST', credentials: 'include' });
      const data = await resp.json();
      if (data?.success) {
        const total    = data.data?.total    ?? data.data?.totalMatches   ?? 0;
        const fetched  = data.data?.matches  ?? data.data?.fetched        ?? 0;
        const inserted = data.data?.inserted ?? data.data?.insertedCount  ?? 0;
        uyar(
          `Cihaz raporu (${startDate} → ${endDate}):\n` +
          `- Cihazda toplam olay: ${total}\n` +
          `- Çekilen: ${fetched}\n` +
          `- DB'ye eklenen (yeni): ${inserted}\n\n` +
          (total === 0 ? `Bu tarih aralığında cihazda hiç olay yok. Daha geniş bir aralık deneyin.` : `Başarılı.`)
        );
      } else {
        uyar('Veri çekme başarısız: ' + (data?.message || 'bilinmeyen hata'));
      }
    } catch (err: any) {
      uyar('Hata: ' + (err.message || err));
    } finally {
      setHikPullingId(null);
    }
  };

  const handlePing = async (device: Device) => {
    setPingingId(device.id);
    try {
      const result = await deviceService.ping(device.id);
      if (result.success) {
        setPingResults(prev => ({ ...prev, [device.id]: result.data }));
        loadData();
      }
    } catch (err) {
      console.error('Ping error:', err);
    } finally {
      setPingingId(null);
    }
  };

  const handlePingAll = async () => {
    for (const device of devices) {
      await handlePing(device);
    }
  };

  // Sync single device
  const handleSyncDevice = async (device: Device) => {
    // 2026-08-10: Hikvision ise ZKTeco path'ine hic girme — Hikvision Veri Cek modalini ac
    // (aksi halde backend zkemkeeper.CZKEM COM instantiate ediyor, REGDB_E_CLASSNOTREG hatasi)
    const proto = (device.protocol || device.deviceType || '').toLowerCase();
    if (proto.includes('hikvision')) {
      openHikvisionPullModal(device);
      return;
    }
    setSyncingDeviceId(device.id);
    try {
      const result = await zktecoService.syncDevice(device.id, { startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() });
      setSyncResult({ type: 'single', data: result });
      loadData();
    } catch (err: any) {
      console.error('Sync error:', err);
      setSyncResult({
        type: 'single',
        data: {
          deviceId: device.id,
          deviceName: device.deviceName,
          success: false,
          error: err.response?.data?.message || err.message || t('devices.syncError')
        }
      });
    } finally {
      setSyncingDeviceId(null);
    }
  };

  // Sync all devices
  const handleSyncAllDevices = async () => {
    setSyncingAll(true);
    try {
      const result = await zktecoService.syncAllDevices({ startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() });
      setSyncResult({ type: 'all', data: result });
      loadData(); // Refresh device list
    } catch (err: any) {
      console.error('Sync all error:', err);
      setSyncResult({
        type: 'all',
        data: {
          totalDevices: 0,
          totalLogs: 0,
          totalSaved: 0,
          results: [],
          error: err.response?.data?.message || err.message || t('devices.bulkSyncError')
        }
      });
    } finally {
      setSyncingAll(false);
    }
  };

  // Secili cihazlardan toplu veri cekme
  const handleBulkSync = async () => {
    const targetIds = selectedDeviceIds.length > 0
      ? selectedDeviceIds
      : devices.filter(d => d.isActive).map(d => d.id);

    if (targetIds.length === 0) return;

    setBulkSyncing(true);
    setBulkSyncResult(null);
    setBulkSyncProgress({ current: 0, total: targetIds.length, deviceName: '', message: t('devices.syncStarting') });

    let sessionId: string | null = null;
    try {
      sessionId = await signalRService.connect();

      signalRService.onProgress((msg) => {
        setBulkSyncProgress({
          current: msg.current || 0,
          total: msg.total || targetIds.length,
          deviceName: msg.deviceName || '',
          message: msg.message || ''
        });
      });

      const result = await zktecoService.syncSelectedDevices(targetIds, {
        startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        sessionId: sessionId || undefined
      });
      setBulkSyncResult(result);
      loadData();
    } catch (err: any) {
      console.error('Bulk sync error:', err);
      setBulkSyncResult({
        totalDevices: targetIds.length,
        successCount: 0,
        failedCount: targetIds.length,
        totalLogs: 0,
        totalSaved: 0,
        results: [{ deviceId: 0, deviceName: t('devices.error'), success: false, error: err.response?.data?.message || err.message }]
      });
    } finally {
      setBulkSyncing(false);
      if (sessionId) {
        try { await signalRService.disconnect(); } catch {}
      }
    }
  };

  // Cihaza personel gonder
  //   mod = 'selected' -> SADECE listede isaretlenen personeller
  //   mod = 'filtered' -> soldaki filtrelere uyan herkes (varsayilan)
  const handleUploadEmployees = async (mod: 'selected' | 'filtered' = 'filtered') => {
    if (!uploadModal) return;
    if (mod === 'selected' && selectedUploadEmpIds.length === 0) {
      uyar('Lütfen listeden en az bir personel seçin.');
      return;
    }
    const secilenIdler = mod === 'selected' ? selectedUploadEmpIds : undefined;
    setUploading(true);
    setUploadResult(null);
    setUploadProgress({ current: 0, total: 0, percent: 0 });

    let sessionId: string | null = null;

    try {
      // SignalR baglantisi kur
      sessionId = await signalRService.connect();

      // Progress dinle
      signalRService.onProgress((message: SyncProgressMessage) => {
        setUploadProgress({
          current: message.current,
          total: message.total,
          percent: message.percent
        });
      });

      // Oncelikle cihazi temizle (eger seciliyse)
      if (uploadForm.clearFirst) {
        setClearing(true);
        try {
          await zktecoService.clearDeviceUsers({
            ipAddress: uploadModal.ipAddress,
            port: uploadModal.port,
            protocol: uploadModal.protocol || 'ZKTeco'
          });
        } catch (clearErr: any) {
          console.error('Clear error:', clearErr);
          uyar(t('devices.clearError') + ': ' + (clearErr.response?.data?.message || clearErr.message));
          setUploading(false);
          setClearing(false);
          await signalRService.disconnect();
          return;
        }
        setClearing(false);
      }

      // 2026-08-13: Hikvision ise ZK path'e girme — Hikvision sync-employees endpoint'ini kullan
      const isHik = (uploadModal.protocol || '').toLowerCase() === 'hikvision';
      let result: any;
      if (isHik) {
        const hikRes: any = await api.post('/hikvision/sync-employees', {
          ip: uploadModal.ipAddress,
          port: uploadModal.port,
          username: (uploadModal as any).username || 'admin',
          password: (uploadModal as any).password || '',
          includeInactive: uploadForm.includeInactive,
          // Tek tek secim varsa backend diger tum filtreleri yok sayar
          employeeIds: secilenIdler,
          departmentIds: (!secilenIdler && uploadForm.departmentId) ? [uploadForm.departmentId] : undefined,
          siteId: secilenIdler ? undefined : (uploadForm.siteId ?? undefined),
          locationId: secilenIdler ? undefined : (uploadForm.locationId ?? undefined),
          dateFilter: (!secilenIdler && uploadForm.dateFilter !== 'all')
            ? (uploadForm.dateFilter === 'today' ? 'today_modified' : 'week_modified')
            : undefined,
        });
        // Backend ApiSuccess({total, succeeded, failed, facesUploaded, cardsUploaded, items})
        const env = hikRes?.data ?? hikRes;
        const d = env?.data ?? env;
        result = {
          success: env?.success !== false,
          message: env?.message,
          totalEmployees: d?.total ?? 0,
          successCount: d?.succeeded ?? 0,
          failedCount: d?.failed ?? 0,
          facesUploaded: d?.facesUploaded ?? 0,
          cardsUploaded: d?.cardsUploaded ?? 0,
          details: d?.items ?? [],
        } as any;
      } else {
        result = await zktecoService.syncEmployeesToDevice({
          ipAddress: uploadModal.ipAddress,
          port: uploadModal.port,
          protocol: uploadModal.protocol || 'ZKTeco',
          employeeIds: secilenIdler,
          departmentId: secilenIdler ? undefined : (uploadForm.departmentId || undefined),
          siteId: secilenIdler ? undefined : (uploadForm.siteId ?? undefined),
          locationId: secilenIdler ? undefined : (uploadForm.locationId ?? undefined),
          includeInactive: uploadForm.includeInactive,
          dateFilter: uploadForm.dateFilter,
          sessionId: sessionId
        } as any);
      }
      setUploadResult(result);
    } catch (err: any) {
      console.error('Upload error:', err);
      uyar(err.response?.data?.message || err.message || t('devices.uploadError'));
    } finally {
      // SignalR baglantisini kapat
      signalRService.offProgress();
      await signalRService.disconnect();
      setUploading(false);
      setClearing(false);
    }
  };

  // Sadece cihazi temizle
  const handleClearDeviceOnly = async () => {
    if (!uploadModal) return;
    if (!(await onayla(t('devices.clearAllConfirm')))) return;

    setClearing(true);
    try {
      await zktecoService.clearDeviceUsers({
        ipAddress: uploadModal.ipAddress,
        port: uploadModal.port,
        protocol: uploadModal.protocol || 'ZKTeco'
      });
      uyar(t('devices.deviceCleared'));
    } catch (err: any) {
      console.error('Clear error:', err);
      uyar(t('devices.clearError') + ': ' + (err.response?.data?.message || err.message));
    } finally {
      setClearing(false);
    }
  };

  // Cihaz teshis testi
  const handleDiagnoseDevice = async (device: Device) => {
    setDiagnoseModal(device);
    setDiagnoseLoading(true);
    setDiagnoseResults([]);
    setDiagnoseSuccess(null);
    try {
      const result = await zktecoService.diagnoseDevice({
        ipAddress: device.ipAddress,
        port: device.port
      });
      setDiagnoseResults(result.results || []);
      setDiagnoseSuccess(result.testUserFound);
    } catch (err: any) {
      console.error('Diagnose error:', err);
      setDiagnoseResults(['HATA: ' + (err.response?.data?.message || err.message)]);
      setDiagnoseSuccess(false);
    } finally {
      setDiagnoseLoading(false);
    }
  };

  // Cihazdaki personel listesini onizle (veritabanina kaydetmeden)
  const handlePreviewDeviceUsers = async (device: Device) => {
    setPreviewModal(device);
    setPreviewLoading(true);
    setPreviewUsers([]);
    setSelectedPreviewUsers([]);
    setEditingCard(null);
    try {
      // 2026-08-13: Hikvision ayri endpoint — ZK path'te takili kaliyordu
      const isHik = (device.protocol || device.deviceType || '').toLowerCase().includes('hikvision');
      if (isHik) {
        const r: any = await api.post('/hikvision/device-users', {
          ip: device.ipAddress,
          port: device.port || 80,
          username: (device as any).username || 'admin',
          password: (device as any).password || '',
          maxResults: 500,
          offset: 0
        });
        const raw = r?.data ?? r;
        const list: any[] = Array.isArray(raw?.users) ? raw.users
          : Array.isArray(raw?.data?.users) ? raw.data.users
          : Array.isArray(raw?.items) ? raw.items
          : Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw) ? raw : [];
        // Hikvision alanlarini ZK preview formatina normalize et
        setPreviewUsers(list.map((u: any) => ({
          enrollNumber: u.employeeNo ?? u.employeeNoString ?? u.enrollNumber ?? '',
          name: u.name ?? u.userName ?? '',
          cardNumber: u.cardNo ?? u.cardNumber ?? '',
          privilege: u.userType === 'admin' ? 14 : 0,
          ...u
        })));
      } else {
        const result = await zktecoService.getDeviceUsers({
          ipAddress: device.ipAddress,
          port: device.port,
          protocol: device.protocol || 'ZKTeco'
        });
        setPreviewUsers(result.users || []);
      }
    } catch (err: any) {
      console.error('Preview error:', err);
      uyar(t('devices.getUsersError') + ': ' + (err.response?.data?.message || err.message));
      setPreviewModal(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Preview modalinda kullanici secimi
  const togglePreviewUserSelection = (enrollNumber: string) => {
    setSelectedPreviewUsers(prev =>
      prev.includes(enrollNumber)
        ? prev.filter(e => e !== enrollNumber)
        : [...prev, enrollNumber]
    );
  };

  const selectAllPreviewUsers = () => {
    setSelectedPreviewUsers(previewUsers.map(u => u.enrollNumber));
  };

  const deselectAllPreviewUsers = () => {
    setSelectedPreviewUsers([]);
  };

  // Secili kullanicilari cihazdan sil
  const handleDeleteSelectedUsers = async () => {
    if (!previewModal || selectedPreviewUsers.length === 0) return;
    if (!(await onayla(`${selectedPreviewUsers.length} kullanici cihazdan silinecek. Emin misiniz?`))) return;

    setDeletingUsers(true);
    try {
      const result = await zktecoService.deleteDeviceUsers({
        ipAddress: previewModal.ipAddress,
        port: previewModal.port,
        enrollNumbers: selectedPreviewUsers
      });

      uyar(`${result.successCount} kullanici silindi, ${result.failedCount} basarisiz`);

      // Listeyi guncelle
      setPreviewUsers(prev => prev.filter(u => !selectedPreviewUsers.includes(u.enrollNumber)));
      setSelectedPreviewUsers([]);
    } catch (err: any) {
      console.error('Delete users error:', err);
      uyar('Silme hatasi: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeletingUsers(false);
    }
  };

  // Kart numarasi guncelle
  const handleUpdateUserCard = async () => {
    if (!previewModal || !editingCard) return;

    setSavingCard(true);
    try {
      await zktecoService.updateUserCard({
        ipAddress: previewModal.ipAddress,
        port: previewModal.port,
        enrollNumber: editingCard.enrollNumber,
        newCardNumber: editingCard.cardNumber
      });

      // Listeyi guncelle
      setPreviewUsers(prev => prev.map(u =>
        u.enrollNumber === editingCard.enrollNumber
          ? { ...u, cardNumber: editingCard.cardNumber }
          : u
      ));
      setEditingCard(null);
      uyar('Kart numarasi guncellendi');
    } catch (err: any) {
      console.error('Update card error:', err);
      uyar('Kart guncelleme hatasi: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingCard(false);
    }
  };

  // Cihazdan kullanicilari veritabanina import et
  const handleImportUsersFromDevice = async () => {
    if (!previewModal) return;

    if (!(await onayla(t('devices.importConfirm')))) {
      return;
    }

    setImportingUsers(true);
    setImportResult(null);
    try {
      // 2026-08-13: Hikvision ise ZK path'e girme (Bellek yetersiz / ZK -7) — ayri endpoint
      const isHik = ((previewModal.protocol || previewModal.deviceType || '') as string).toLowerCase().includes('hikvision');
      let response: any;
      if (isHik) {
        const r: any = await api.post('/hikvision/import-users-to-db', {
          ip: previewModal.ipAddress,
          port: previewModal.port || 80,
          username: (previewModal as any).username || 'admin',
          password: (previewModal as any).password || '',
          maxResults: 1000
        });
        const env = r?.data ?? r;
        const d = env?.data ?? env;
        response = {
          success: env?.success !== false,
          message: typeof env?.message === 'string'
            ? env.message
            : `${d?.added ?? 0} eklendi, ${d?.updated ?? 0} guncellendi${d?.failed ? `, ${d.failed} hata` : ''}`,
          data: {
            totalOnDevice: d?.total ?? d?.totalOnDevice ?? ((d?.added ?? 0) + (d?.updated ?? 0)),
            imported: d?.added ?? d?.imported ?? 0,
            skipped: d?.updated ?? d?.skipped ?? 0,
            // items: [{employeeNo, name, action, id}] → string[] (React child object hatasi olmasin)
            importedUsers: Array.isArray(d?.items)
              ? d.items.map((u: any) => typeof u === 'string' ? u : `${u?.employeeNo ?? ''} - ${u?.name ?? ''}${u?.action ? ` (${u.action})` : ''}`)
              : (Array.isArray(d?.importedUsers) ? d.importedUsers.map((u: any) => typeof u === 'string' ? u : String(u?.name ?? u?.employeeNo ?? '')) : []),
            errors: Array.isArray(d?.errors)
              ? d.errors.map((e: any) => typeof e === 'string' ? e : (e?.error ?? e?.message ?? JSON.stringify(e)))
              : [],
          }
        };
      } else {
        response = await deviceService.importUsers(previewModal.deviceId);
      }

      if (response.success) {
        setImportResult({
          success: true,
          message: response.message,
          totalOnDevice: response.data.totalOnDevice,
          imported: response.data.imported,
          skipped: response.data.skipped,
          importedUsers: response.data.importedUsers,
          errors: response.data.errors
        });

        if (response.data.imported > 0) {
          // Listeyi yenile
          await handlePreviewDeviceUsers(previewModal);
        }
      } else {
        setImportResult({
          success: false,
          message: response.message || t('devices.importFailed')
        });
      }
    } catch (err: any) {
      console.error('Import users error:', err);
      setImportResult({
        success: false,
        message: t('devices.importError') + ': ' + (err.response?.data?.message || err.message)
      });
    } finally {
      setImportingUsers(false);
    }
  };

  // Cihazdan veri oku
  const handleDownloadLogs = async () => {
    if (!downloadModal) return;
    setDownloading(true);
    setDownloadResult(null);
    try {
      const result = await zktecoService.fetchLogsFromDevice({
        deviceId: downloadModal.deviceId,
        ipAddress: downloadModal.ipAddress,
        port: downloadModal.port,
        protocol: downloadModal.protocol || 'ZKTeco',
        startDate: downloadForm.startDate ? new Date(downloadForm.startDate).toISOString() : undefined
      });
      setDownloadResult(result);
      loadData(); // Refresh device list
    } catch (err: any) {
      console.error('Download error:', err);
      uyar(err.response?.data?.message || err.message || t('devices.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  const openUploadModal = (device: Device) => {
    setUploadModal(device);
    setUploadForm({ departmentId: null, siteId: null, locationId: null, includeInactive: false, clearFirst: false, dateFilter: 'all' });
    setUploadResult(null);
    setUploadProgress({ current: 0, total: 0, percent: 0 });
  };

  // Cihaz secim fonksiyonlari
  const toggleDeviceSelection = (deviceId: number) => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const selectAllDevices = () => {
    const activeDeviceIds = devices.filter(d => d.isActive).map(d => d.id);
    setSelectedDeviceIds(activeDeviceIds);
  };

  const deselectAllDevices = () => {
    setSelectedDeviceIds([]);
  };

  // Toplu yukleme modali ac
  const openBulkUploadModal = () => {
    setBulkUploadModal(true);
    setUploadForm({ departmentId: null, siteId: null, locationId: null, includeInactive: false, clearFirst: false, dateFilter: 'all' });
    setUploadResult(null);
    setUploadProgress({ current: 0, total: 0, percent: 0 });
  };

  // Secili veya tum cihazlara personel gonder
  const handleBulkUploadEmployees = async (sendToAll: boolean) => {
    const targetDevices = sendToAll
      ? devices.filter(d => d.isActive)
      : devices.filter(d => selectedDeviceIds.includes(d.id) && d.isActive);

    if (targetDevices.length === 0) {
      uyar(t('devices.noActiveDevices'));
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setUploadProgress({ current: 0, total: 0, percent: 0 });

    let totalSuccess = 0;
    let totalFailed = 0;
    const results: any[] = [];

    for (let i = 0; i < targetDevices.length; i++) {
      const device = targetDevices[i];

      // Cihaz bazinda progress goster
      setUploadProgress({
        current: i + 1,
        total: targetDevices.length,
        percent: Math.round(((i + 1) / targetDevices.length) * 100)
      });

      try {
        // SignalR baglantisi kur
        const sessionId = await signalRService.connect();

        // Temizle (eger seciliyse)
        if (uploadForm.clearFirst) {
          try {
            await zktecoService.clearDeviceUsers({
              ipAddress: device.ipAddress,
              port: device.port,
              protocol: device.protocol || 'ZKTeco'
            });
          } catch (clearErr: any) {
            console.error(`Clear error for ${device.deviceName}:`, clearErr);
          }
        }

        const result = await zktecoService.syncEmployeesToDevice({
          ipAddress: device.ipAddress,
          port: device.port,
          protocol: device.protocol || 'ZKTeco',
          departmentId: uploadForm.departmentId || undefined,
          includeInactive: uploadForm.includeInactive,
          dateFilter: uploadForm.dateFilter,
          sessionId: sessionId
        });

        totalSuccess += result.successCount;
        totalFailed += result.failedCount;
        results.push({ device: device.deviceName, ...result, success: true });

        await signalRService.disconnect();
      } catch (err: any) {
        console.error(`Upload error for ${device.deviceName}:`, err);
        results.push({ device: device.deviceName, error: err.message, success: false });
        totalFailed++;
      }
    }

    setUploadResult({
      totalEmployees: totalSuccess + totalFailed,
      successCount: totalSuccess,
      failedCount: totalFailed,
      users: []
    });

    setUploading(false);
  };

  const openDownloadModal = (device: Device) => {
    setDownloadModal(device);
    setDownloadForm({ startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] });
    setDownloadResult(null);
  };

  const openCreateModal = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedDevice(null);
    setModalMode('create');
  };

  const openEditModal = (device: Device) => {
    setFormData({
      deviceName: device.deviceName,
      deviceCode: device.deviceCode || '',
      ipAddress: device.ipAddress,
      port: device.port,
      locationId: device.locationId || null,
      location: device.location || '',
      deviceType: device.deviceType || 'ZKTeco',
      protocol: device.protocol || 'ZKTeco',
      serialNumber: device.serialNumber || '',
      model: device.model || '',
      macAddress: device.macAddress || '',
      isActive: device.isActive,
      // Hikvision cihaz admin (2026-08-08): edit modda mevcut degerlerle doldur
      username: (device as any).username || '',
      password: (device as any).password && (device as any).password !== '0' ? (device as any).password : ''
    } as any);
    setFormErrors({});
    setSelectedDevice(device);
    setModalMode('edit');
  };

  const openViewModal = (device: Device) => {
    setSelectedDevice(device);
    setModalMode('view');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedDevice(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.deviceName.trim()) {
      errors.deviceName = t('devices.deviceNameRequired');
    }

    if (!formData.ipAddress.trim()) {
      errors.ipAddress = t('devices.ipRequired');
    } else if (!isValidIpAddress(formData.ipAddress)) {
      errors.ipAddress = t('devices.ipInvalid');
    }

    if (formData.macAddress && !isValidMacAddress(formData.macAddress)) {
      errors.macAddress = t('devices.macInvalid');
    }

    if (formData.port < 1 || formData.port > 65535) {
      errors.port = t('devices.portRange');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLocationChange = (locationId: string) => {
    const id = locationId ? parseInt(locationId) : null;
    const selectedLocation = locations.find(l => l.id === id);
    setFormData({
      ...formData,
      locationId: id,
      location: selectedLocation?.name || ''
    });
  };

  const handleMacAddressChange = (value: string) => {
    // Auto-format MAC address as user types
    let formatted = value.toUpperCase().replace(/[^0-9A-F]/g, '');
    if (formatted.length > 12) formatted = formatted.slice(0, 12);

    // Add colons every 2 characters
    if (formatted.length > 2) {
      formatted = formatted.match(/.{1,2}/g)?.join(':') || formatted;
    }

    setFormData({ ...formData, macAddress: formatted });

    // Clear error if valid
    if (formatted && !isValidMacAddress(formatted) && formatted.length === 17) {
      setFormErrors(prev => ({ ...prev, macAddress: '' }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Format MAC address before saving
      const formattedMac = formatMacAddress(formData.macAddress);

      if (modalMode === 'create') {
        const createData: DeviceCreateDto = {
          deviceName: formData.deviceName,
          deviceCode: formData.deviceCode || undefined,
          ipAddress: formData.ipAddress,
          port: formData.port,
          locationId: formData.locationId || undefined,
          location: formData.location || undefined,
          deviceType: formData.deviceType,
          protocol: formData.protocol,
          serialNumber: formData.serialNumber || undefined,
          model: formData.model || undefined,
          macAddress: formattedMac || undefined,
          // Hikvision admin (2026-08-08)
          username: (formData as any).username || undefined,
          password: (formData as any).password || undefined
        } as any;
        const result = await deviceService.create(createData);
        if (result.success) {
          closeModal();
          loadData();
        } else {
          setError(result.message || t('devices.addError'));
        }
      } else if (modalMode === 'edit' && selectedDevice) {
        const updateData: DeviceUpdateDto = {
          deviceName: formData.deviceName,
          deviceCode: formData.deviceCode || undefined,
          ipAddress: formData.ipAddress,
          port: formData.port,
          locationId: formData.locationId || undefined,
          location: formData.location || undefined,
          deviceType: formData.deviceType,
          protocol: formData.protocol,
          serialNumber: formData.serialNumber || undefined,
          model: formData.model || undefined,
          macAddress: formattedMac || undefined,
          isActive: formData.isActive,
          // Hikvision admin (2026-08-08) — bos gonderme, edit'te "sifreyi degistirme" senaryosunu koru
          username: (formData as any).username || undefined,
          password: (formData as any).password || undefined
        } as any;
        const result = await deviceService.update(selectedDevice.id, updateData);
        if (result.success) {
          closeModal();
          loadData();
        } else {
          setError(result.message || t('devices.updateError'));
        }
      }
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || t('devices.operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      const result = await deviceService.delete(deleteConfirm.id);
      if (result.success) {
        setDeleteConfirm(null);
        loadData();
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      uyar(err.response?.data?.message || t('devices.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const getStatusConfig = (device: Device) => {
    if (device.isOnline) {
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        dot: 'bg-green-500',
        label: t('devices.online'),
        icon: Wifi
      };
    }
    return {
      color: 'text-slate-500',
      bg: 'bg-slate-50',
      dot: 'bg-slate-400',
      label: t('devices.offline'),
      icon: WifiOff
    };
  };

  const getLocationName = (device: Device): string => {
    if (device.locationId) {
      const loc = locations.find(l => l.id === device.locationId);
      return loc?.name || device.location || '-';
    }
    return device.location || '-';
  };

  const filteredDevices = devices.filter(d =>
    d.deviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.location && d.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (d.serialNumber && d.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error && devices.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('devices.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('devices.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">
            {stats ? t('devices.subtitle', { online: stats.online, total: stats.total }) : t('devices.subtitleLoading')}
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <input
            type="text"
            placeholder={t('devices.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm w-full md:w-48 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'card' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <List size={18} />
            </button>
          </div>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium"
          >
            <RefreshCw size={16} /> {t('devices.refresh')}
          </button>
          <button
            onClick={handlePingAll}
            disabled={!!pingingId}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium disabled:opacity-50"
          >
            <Radio size={16} className={pingingId ? 'animate-pulse text-blue-500' : ''} /> {t('devices.pingAll')}
          </button>
          <button
            onClick={handleSyncAllDevices}
            disabled={syncingAll || !!syncingDeviceId}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
          >
            {syncingAll ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            {t('devices.syncAll')}
          </button>

          {/* Secili Cihazlardan Veri Cek */}
          {selectedDeviceIds.length > 0 && (
            <button
              onClick={() => { setBulkSyncModal(true); setBulkSyncResult(null); }}
              disabled={bulkSyncing || syncingAll || !!syncingDeviceId}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium disabled:opacity-50"
            >
              <Database size={16} />
              {t('devices.fetchFromSelected', { count: selectedDeviceIds.length })}
            </button>
          )}

          {/* Toplu Personel Gonderme Butonlari */}
          {selectedDeviceIds.length > 0 && (
            <button
              onClick={openBulkUploadModal}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
            >
              <Upload size={16} />
              {t('devices.sendToSelected', { count: selectedDeviceIds.length })}
            </button>
          )}
          <button
            onClick={() => { setSelectedDeviceIds(devices.filter(d => d.isActive).map(d => d.id)); openBulkUploadModal(); }}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
          >
            <Users size={16} />
            {t('devices.sendToAllDevices')}
          </button>

          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus size={16} /> {t('devices.addDevice')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Server size={20} className="text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase">{t('devices.total')}</p>
                <p className="text-2xl font-black text-slate-800">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Wifi size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase">{t('devices.online')}</p>
                <p className="text-2xl font-black text-green-600">{stats.online}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <WifiOff size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase">{t('devices.offline')}</p>
                <p className="text-2xl font-black text-red-600">{stats.offline}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle2 size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase">{t('devices.active')}</p>
                <p className="text-2xl font-black text-blue-600">{stats.active}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card View */}
      {/* Secim Kontrolleri */}
      {devices.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded-xl">
          <span className="text-sm font-medium text-slate-600">{t('devices.deviceSelection')}</span>
          <button
            onClick={selectAllDevices}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t('devices.selectAll')}
          </button>
          <button
            onClick={deselectAllDevices}
            className="text-sm text-slate-500 hover:text-slate-700 font-medium"
          >
            {t('devices.deselectAll')}
          </button>
          {selectedDeviceIds.length > 0 && (
            <span className="ml-auto px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              {t('devices.devicesSelected', { count: selectedDeviceIds.length })}
            </span>
          )}
        </div>
      )}

      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map(device => {
            const status = getStatusConfig(device);
            const pingInfo = pingResults[device.id];
            const isPinging = pingingId === device.id;
            const StatusIcon = status.icon;

            return (
              <div
                key={device.id}
                className={`bg-white rounded-2xl shadow-sm border p-6 relative overflow-hidden group transition-all hover:shadow-lg ${
                  selectedDeviceIds.includes(device.id)
                    ? 'border-amber-400 ring-2 ring-amber-100'
                    : 'border-slate-200 hover:border-blue-400'
                }`}
              >
                <div className={`absolute top-0 left-0 w-1.5 h-full ${status.dot}`}></div>

                {/* Cihaz Secim Checkbox */}
                <div className="absolute top-3 right-3 z-10">
                  <input
                    type="checkbox"
                    checked={selectedDeviceIds.includes(device.id)}
                    onChange={() => toggleDeviceSelection(device.id)}
                    className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                  />
                </div>

                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-4">
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <HardDrive size={24} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-800">{device.deviceName}</h3>
                      <div className="text-sm text-slate-500 font-mono bg-slate-50 px-2 py-0.5 rounded mt-1">
                        {device.ipAddress}:{device.port}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openViewModal(device)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title={t('devices.detail')}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => openEditModal(device)}
                      className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                      title={t('common.edit')}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(device)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title={t('common.delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {getLocationName(device) !== '-' && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <MapPin size={14} className="text-blue-500" />
                    <span>{getLocationName(device)}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    (device.protocol || 'ZKTeco') === 'ZKTeco'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-purple-50 text-purple-700'
                  }`}>
                    <Radio size={10} />
                    {device.protocol || 'ZKTeco'}
                  </span>
                  {device.deviceType && device.deviceType !== device.protocol && (
                    <span className="text-slate-500">{device.deviceType}</span>
                  )}
                  {device.model && (
                    <span className="text-slate-400">• {device.model}</span>
                  )}
                </div>

                <div className="flex justify-between items-center mb-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold ${status.bg} ${status.color}`}>
                    <StatusIcon size={14} />
                    {status.label}
                  </div>
                </div>

                {(device.lastSyncTime || device.lastConnectionTime) && (
                  <div className="flex flex-col gap-1 mb-4 p-2 bg-slate-50 rounded-lg">
                    {device.lastSyncTime && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 flex items-center gap-1">
                          <Download size={12} />
                          {t('devices.lastDataDownloadLabel')}
                        </span>
                        <span className="text-slate-700 font-medium">
                          {new Date(device.lastSyncTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {device.lastConnectionTime && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 flex items-center gap-1">
                          <Wifi size={12} />
                          {t('devices.lastConnectionLabel')}
                        </span>
                        <span className="text-slate-700 font-medium">
                          {new Date(device.lastConnectionTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {pingInfo && (
                  <div className={`mb-4 p-3 rounded-lg text-xs font-medium ${pingInfo.isOnline ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {pingInfo.isOnline ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle2 size={14} /> {t('devices.reachable', { ms: pingInfo.latency })}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <AlertCircle size={14} /> {t('devices.unreachable')}
                      </span>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    onClick={() => handlePing(device)}
                    disabled={isPinging}
                    className="py-2 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    {isPinging ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Radio size={12} />
                    )}
                    Ping
                  </button>
                  <button
                    onClick={() => handleSyncDevice(device)}
                    disabled={syncingDeviceId === device.id || syncingAll}
                    className="py-2 px-3 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    {syncingDeviceId === device.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Download size={12} />
                    )}
                    {t('devices.cardViewSync')}
                  </button>
                </div>
                {/* Ag 2026: Hikvision cihaz icin tarih araligi ile Veri Cek — event-push mimarisi disinda ek yol */}
                {(device.protocol === 'Hikvision' || device.deviceType === 'Hikvision') && (
                  <button
                    onClick={() => openHikvisionPullModal(device)}
                    disabled={hikPullingId === device.id}
                    className="w-full py-2 px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 disabled:opacity-50 flex items-center justify-center gap-1.5 mb-2"
                    title="Cihazdan tarih araligi secip giris-cikis olaylarini cek"
                  >
                    {hikPullingId === device.id
                      ? <><Loader2 size={12} className="animate-spin" /> Çekiliyor...</>
                      : <><Download size={12} /> Veri Çek (Hikvision)</>}
                  </button>
                )}
                {/* 2026-08-15 (kullanici talebi): Hikvision cihazda ZKTeco'ya OZEL
                    butonlar gizlenir. Bunlar zkemkeeper COM uzerinden calisir ve
                    Hikvision cihazda hata verir; kullanici yanlislikla tikliyordu.
                      - Veri Oku        -> zktecoService.fetchLogsFromDevice
                      - Personel Gonder -> zktecoService.clearDeviceUsers
                      - Tanilama Testi  -> zktecoService.diagnoseDevice
                    "Cihaz Personelleri" her iki protokolu de destekledigi icin kalir. */}
                {/* 2026-08-15 DUZELTME: "Personel Gonder" Hikvision'i DESTEKLIYOR
                    (openUploadModal icinde /hikvision/sync-employees dali var);
                    yanlislikla gizlenmisti, geri acildi.
                    "Veri Oku" ise yalnizca zktecoService.fetchLogsFromDevice
                    cagiriyor — Hikvision'da "Veri Cek (Hikvision)" kullanilir. */}
                <div className={isHikvisionCihaz(device) ? '' : 'grid grid-cols-2 gap-2'}>
                  {!isHikvisionCihaz(device) && (
                    <button
                      onClick={() => openDownloadModal(device)}
                      className="py-2 px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 flex items-center justify-center gap-1.5"
                    >
                      <Download size={12} />
                      {t('devices.cardViewReadData')}
                    </button>
                  )}
                  <button
                    onClick={() => openUploadModal(device)}
                    className={`py-2 px-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 flex items-center justify-center gap-1.5 ${
                      isHikvisionCihaz(device) ? 'w-full' : ''
                    }`}
                  >
                    <Upload size={12} />
                    {t('devices.cardViewSendEmployees')}
                  </button>
                </div>
                <button
                  onClick={() => handlePreviewDeviceUsers(device)}
                  className="w-full py-2 px-3 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-xs font-bold hover:bg-purple-100 flex items-center justify-center gap-1.5"
                >
                  <Users size={12} />
                  {t('devices.cardViewDeviceEmployees')}
                </button>
                {!isHikvisionCihaz(device) && (
                  <button
                    onClick={() => handleDiagnoseDevice(device)}
                    className="w-full py-2 px-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100 flex items-center justify-center gap-1.5"
                  >
                    <Activity size={12} />
                    {t('devices.cardViewDiagnostic')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderDevice')}</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderIpAddress')}</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderLocation')}</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderTypeModel')}</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderStatus')}</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderLastConnection')}</th>
                <th className="text-right px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('devices.listHeaderActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDevices.map(device => {
                const status = getStatusConfig(device);
                const isPinging = pingingId === device.id;
                const StatusIcon = status.icon;

                return (
                  <tr key={device.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <HardDrive size={18} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{device.deviceName}</p>
                          {device.deviceCode && (
                            <p className="text-xs text-slate-400">{device.deviceCode}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {device.ipAddress}:{device.port}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getLocationName(device) !== '-' ? (
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <MapPin size={14} className="text-blue-500" />
                          {getLocationName(device)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                          (device.protocol || 'ZKTeco') === 'ZKTeco'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-purple-50 text-purple-700'
                        }`}>
                          {device.protocol || 'ZKTeco'}
                        </span>
                        <span className="text-sm text-slate-500">
                          {device.deviceType && device.deviceType !== device.protocol ? device.deviceType : ''}
                          {device.model ? ` ${device.model}` : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold ${status.bg} ${status.color}`}>
                        <StatusIcon size={14} />
                        {status.label}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {device.lastConnectionTime ? (
                        <span className="text-sm text-slate-500">
                          {new Date(device.lastConnectionTime).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handlePing(device)}
                          disabled={isPinging}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                          title="Ping"
                        >
                          {isPinging ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Radio size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleSyncDevice(device)}
                          disabled={syncingDeviceId === device.id || syncingAll}
                          className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                          title={t('devices.syncConfirm')}
                        >
                          {syncingDeviceId === device.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                        {/* 2026-08-15: Hikvision'da ZKTeco'ya ozel islemler gizli
                            (liste gorunumu de kart gorunumuyle ayni davransin) */}
                        {/* Veri Oku ZKTeco'ya ozel; Personel Gonder her iki protokolde de calisir */}
                        {!isHikvisionCihaz(device) && (
                          <button
                            onClick={() => openDownloadModal(device)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title={t('devices.readFromDevice')}
                          >
                            <Database size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => openUploadModal(device)}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title={t('devices.sendToDevice')}
                        >
                          <UserPlus size={16} />
                        </button>
                        <button
                          onClick={() => handlePreviewDeviceUsers(device)}
                          className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title={t('devices.viewDeviceEmployees')}
                        >
                          <Users size={16} />
                        </button>
                        <button
                          onClick={() => openViewModal(device)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title={t('devices.detail')}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => openEditModal(device)}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title={t('common.edit')}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(device)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title={t('common.delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredDevices.length === 0 && (
            <div className="text-center py-12">
              <HardDrive className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{t('devices.noDeviceFound')}</p>
            </div>
          )}
        </div>
      )}

      {devices.length === 0 && !loading && (
        <div className="text-center py-12">
          <HardDrive className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-800">{t('devices.noDevices')}</h3>
          <p className="text-slate-500 text-sm mt-2">{t('devices.noDevicesDescription')}</p>
          <button
            onClick={openCreateModal}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            {t('devices.addFirstDevice')}
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(modalMode === 'create' || modalMode === 'edit') && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  {modalMode === 'create' ? t('devices.newDevice') : t('devices.editDeviceTitle')}
                </h2>
                <p className="text-sm text-slate-500">{t('devices.enterTerminalInfo')}</p>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('devices.deviceNameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.deviceName}
                    onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${formErrors.deviceName ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    placeholder={t('devices.deviceNamePlaceholder')}
                  />
                  {formErrors.deviceName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.deviceName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.deviceCodeLabel')}</label>
                  <input
                    type="text"
                    value={formData.deviceCode}
                    onChange={(e) => setFormData({ ...formData, deviceCode: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="DEV-001"
                  />
                </div>
              </div>

              {/* Network Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('devices.ipAddressLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.ipAddress}
                    onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${formErrors.ipAddress ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    placeholder="192.168.1.100"
                  />
                  {formErrors.ipAddress && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.ipAddress}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.portLabel')}</label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 4370 })}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${formErrors.port ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    min={1}
                    max={65535}
                  />
                  {formErrors.port && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.port}</p>
                  )}
                </div>
              </div>

              {/* Protocol & Device Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('devices.protocolLabel')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.protocol}
                    onChange={(e) => {
                      const selectedProtocol = PROTOCOLS.find(p => p.value === e.target.value);
                      setFormData({
                        ...formData,
                        protocol: e.target.value,
                        port: selectedProtocol?.defaultPort || formData.port
                      });
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {PROTOCOLS.map(protocol => (
                      <option key={protocol.value} value={protocol.value}>{protocol.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Varsayilan port: {PROTOCOLS.find(p => p.value === formData.protocol)?.defaultPort ?? formData.port}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.deviceTypeLabel')}</label>
                  <select
                    value={formData.deviceType}
                    onChange={(e) => setFormData({ ...formData, deviceType: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {DEVICE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Cihaz markasi. Iletisim icin "Protokol" alani kullanilir.</p>
                </div>
              </div>

              {/* Hikvision seçilirse: Cihaz Admin (kullanici/sifre) — ISAPI kimlik dogrulama */}
              {formData.protocol === 'Hikvision' && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <p className="text-sm font-bold text-emerald-800 mb-2">Hikvision Cihaz Admin Erisimi</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Kullanici Adi</label>
                      <input
                        type="text"
                        value={(formData as any).username || ''}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value } as any)}
                        placeholder="admin"
                        className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Sifre</label>
                      <input
                        type="password"
                        value={(formData as any).password || ''}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value } as any)}
                        placeholder="cihaz admin sifresi"
                        className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-emerald-800/70 mt-2">
                    ISAPI komutlari (push host yaz, personel listele/yukle, log cek) icin kullanilir. NAT senaryosunda farkli bir port girilebilir.
                  </p>
                </div>
              )}

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.modelLabel')}</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="K40, F18, SpeedFace, T-10..."
                />
              </div>

              {/* Serial & MAC */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.serialNumberLabel')}</label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    placeholder="SN123456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.macAddressLabel')}</label>
                  <input
                    type="text"
                    value={formData.macAddress}
                    onChange={(e) => handleMacAddressChange(e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${formErrors.macAddress ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    placeholder="00:1A:2B:3C:4D:5E"
                    maxLength={17}
                  />
                  {formErrors.macAddress && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.macAddress}</p>
                  )}
                </div>
              </div>

              {/* Location Dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.locationLabel')}</label>
                <select
                  value={formData.locationId || ''}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('devices.selectLocationPlaceholder')}</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} {loc.code && `(${loc.code})`}
                    </option>
                  ))}
                </select>
                {locations.length === 0 && (
                  <p className="text-slate-400 text-xs mt-1">{t('devices.noLocationsYet')}</p>
                )}
              </div>

              {/* Active Status (only in edit mode) */}
              {modalMode === 'edit' && (
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-slate-700">
                    {t('devices.activeDeviceCheckbox')}
                  </label>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3 sticky bottom-0 bg-white">
              <button
                onClick={closeModal}
                className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {t('devices.cancelButton')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {modalMode === 'create' ? t('devices.saveButton') : t('devices.updateButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {modalMode === 'view' && selectedDevice && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-800">{t('devices.deviceDetail')}</h2>
                <p className="text-sm text-slate-500">{selectedDevice.deviceName}</p>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewDeviceName')}</p>
                  <p className="text-slate-800 font-medium">{selectedDevice.deviceName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewDeviceCode')}</p>
                  <p className="text-slate-800 font-medium">{selectedDevice.deviceCode || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewIpAddress')}</p>
                  <p className="text-slate-800 font-mono">{selectedDevice.ipAddress}:{selectedDevice.port}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewProtocol')}</p>
                  <p className="text-slate-800 font-medium">{selectedDevice.protocol || 'ZKTeco'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewDeviceType')}</p>
                  <p className="text-slate-800 font-medium">{selectedDevice.deviceType || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewModel')}</p>
                  <p className="text-slate-800 font-medium">{selectedDevice.model || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewSerialNo')}</p>
                  <p className="text-slate-800 font-mono text-sm">{selectedDevice.serialNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewMacAddress')}</p>
                  <p className="text-slate-800 font-mono text-sm">{selectedDevice.macAddress || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewLocation')}</p>
                  <p className="text-slate-800 font-medium">{getLocationName(selectedDevice)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewStatus')}</p>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-bold mt-1 ${selectedDevice.isOnline ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                    {selectedDevice.isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
                    {selectedDevice.isOnline ? t('devices.viewOnline') : t('devices.viewOffline')}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewActive')}</p>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-bold mt-1 ${selectedDevice.isActive ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                    {selectedDevice.isActive ? t('devices.viewActiveYes') : t('devices.viewActiveNo')}
                  </div>
                </div>
                {selectedDevice.currentUsers !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewRegisteredUsers')}</p>
                    <p className="text-slate-800 font-medium flex items-center gap-1">
                      <Users size={14} /> {selectedDevice.currentUsers}
                    </p>
                  </div>
                )}
                {selectedDevice.lastConnectionTime && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewLastConnection')}</p>
                    <p className="text-slate-800 text-sm">{new Date(selectedDevice.lastConnectionTime).toLocaleString()}</p>
                  </div>
                )}
                {selectedDevice.lastSyncTime && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewLastSync')}</p>
                    <p className="text-slate-800 text-sm">{new Date(selectedDevice.lastSyncTime).toLocaleString()}</p>
                  </div>
                )}
                {selectedDevice.createdDate && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold">{t('devices.viewCreatedDate')}</p>
                    <p className="text-slate-800 text-sm">{new Date(selectedDevice.createdDate).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {t('devices.closeButton')}
              </button>
              <button
                onClick={() => {
                  closeModal();
                  openEditModal(selectedDevice);
                }}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Pencil size={16} /> {t('devices.editButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">{t('devices.deleteDeviceTitle')}</h3>
              <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: t('devices.deleteDeviceConfirmText', { name: deleteConfirm.deviceName }) }} />
              <p className="text-sm text-slate-500 mt-2">{t('devices.deleteIrreversibleText')}</p>
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                {t('devices.cancelButton')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
                {t('devices.deleteButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Sync Modal */}
      {bulkSyncModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <Database className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{t('devices.bulkSyncTitle2')}</h2>
                  <p className="text-sm text-slate-500">
                    {selectedDeviceIds.length > 0
                      ? t('devices.bulkSyncDevicesSelected', { count: selectedDeviceIds.length })
                      : t('devices.bulkSyncActiveDevices', { count: devices.filter(d => d.isActive).length })}
                  </p>
                </div>
              </div>
              {!bulkSyncing && (
                <button onClick={() => { setBulkSyncModal(false); setBulkSyncResult(null); }} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              )}
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {!bulkSyncing && !bulkSyncResult && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    {t('devices.bulkSyncDescription2')}
                  </p>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm font-bold text-slate-700 mb-2">{t('devices.bulkSyncDevicesToSync')}</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {(selectedDeviceIds.length > 0
                        ? devices.filter(d => selectedDeviceIds.includes(d.id))
                        : devices.filter(d => d.isActive)
                      ).map(d => (
                        <div key={d.id} className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${d.isOnline ? 'bg-green-500' : 'bg-slate-300'}`} />
                          <span className="font-medium">{d.deviceName}</span>
                          <span className="text-slate-400">{d.ipAddress}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {bulkSyncing && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Loader2 size={20} className="animate-spin text-teal-600" />
                    <div>
                      <p className="font-medium text-slate-800">{bulkSyncProgress.deviceName || t('devices.bulkSyncStarting')}</p>
                      <p className="text-sm text-slate-500">{bulkSyncProgress.message}</p>
                    </div>
                  </div>
                  <div className="bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-teal-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${bulkSyncProgress.total > 0 ? (bulkSyncProgress.current / bulkSyncProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-center text-slate-500">
                    {t('devices.bulkSyncDevicesProcessed', { current: bulkSyncProgress.current, total: bulkSyncProgress.total })}
                  </p>
                </div>
              )}

              {bulkSyncResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-green-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.bulkSyncSuccessful')}</p>
                      <p className="text-2xl font-black text-green-600">{bulkSyncResult.successCount}</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-red-600 uppercase font-bold mb-1">{t('devices.bulkSyncFailed')}</p>
                      <p className="text-2xl font-black text-red-600">{bulkSyncResult.failedCount}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-blue-600 uppercase font-bold mb-1">{t('devices.bulkSyncNewRecords')}</p>
                      <p className="text-2xl font-black text-blue-600">{bulkSyncResult.totalSaved}</p>
                    </div>
                  </div>

                  {bulkSyncResult.results && bulkSyncResult.results.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2">{t('devices.bulkSyncDeviceDetails')}</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {bulkSyncResult.results.map((r: any, i: number) => (
                          <div
                            key={i}
                            className={`flex items-center justify-between p-3 rounded-lg ${r.success ? 'bg-green-50' : 'bg-red-50'}`}
                          >
                            <div className="flex items-center gap-2">
                              {r.success ? (
                                <CheckCircle2 size={16} className="text-green-600" />
                              ) : (
                                <AlertCircle size={16} className="text-red-600" />
                              )}
                              <span className="font-medium text-sm">{r.deviceName}</span>
                            </div>
                            <div className="text-xs text-slate-600">
                              {r.success ? t('devices.bulkSyncFoundSaved', { found: r.logsFound || 0, saved: r.logsSaved || 0 }) : (r.error || t('devices.error'))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3">
              {!bulkSyncing && !bulkSyncResult && (
                <>
                  <button
                    onClick={() => { setBulkSyncModal(false); }}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200"
                  >
                    {t('devices.cancelButton')}
                  </button>
                  <button
                    onClick={handleBulkSync}
                    className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 flex items-center justify-center gap-2"
                  >
                    <Play size={16} /> {t('devices.startSyncButton')}
                  </button>
                </>
              )}
              {bulkSyncResult && (
                <button
                  onClick={() => { setBulkSyncModal(false); setBulkSyncResult(null); }}
                  className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800"
                >
                  {t('devices.okButton')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sync Result Modal */}
      {syncResult && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  syncResult.type === 'single'
                    ? (syncResult.data.error ? 'bg-red-100' : 'bg-green-100')
                    : 'bg-blue-100'
                }`}>
                  {syncResult.data.error ? (
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  ) : (
                    <Database className={`w-6 h-6 ${syncResult.type === 'single' ? 'text-green-600' : 'text-blue-600'}`} />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">
                    {syncResult.type === 'single' ? t('devices.syncResultTitle') : t('devices.bulkSyncResultTitle')}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {syncResult.type === 'single'
                      ? syncResult.data.deviceName
                      : t('devices.syncResultDevicesProcessed', { count: syncResult.data.totalDevices || 0 })}
                  </p>
                </div>
              </div>
              <button onClick={() => setSyncResult(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {syncResult.data.error ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                  <p className="font-semibold">{t('devices.syncResultError')}</p>
                  <p className="text-sm mt-1">{syncResult.data.error}</p>
                </div>
              ) : syncResult.type === 'single' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">{t('devices.syncResultRecordsFound')}</p>
                      <p className="text-2xl font-black text-slate-800">{syncResult.data.logsFound || 0}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.syncResultRecordsSaved')}</p>
                      <p className="text-2xl font-black text-green-600">{syncResult.data.logsSaved || 0}</p>
                    </div>
                  </div>
                  {syncResult.data.serialNumber && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">{t('devices.syncResultSerialNo')}</p>
                          <p className="font-mono font-medium">{syncResult.data.serialNumber}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">{t('devices.syncResultRegisteredUsers')}</p>
                          <p className="font-medium">{syncResult.data.userCount || 0}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <Clock size={14} />
                    {new Date(syncResult.data.syncTime).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">{t('devices.syncResultTotalDevices')}</p>
                      <p className="text-2xl font-black text-slate-800">{syncResult.data.totalDevices || 0}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-blue-600 uppercase font-bold mb-1">{t('devices.syncResultTotalLogs')}</p>
                      <p className="text-2xl font-black text-blue-600">{syncResult.data.totalLogs || 0}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.syncResultSaved')}</p>
                      <p className="text-2xl font-black text-green-600">{syncResult.data.totalSaved || 0}</p>
                    </div>
                  </div>

                  {syncResult.data.results && syncResult.data.results.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-bold text-slate-700 mb-2">{t('devices.syncResultDeviceDetails')}</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {syncResult.data.results.map((r: any, i: number) => (
                          <div
                            key={i}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              r.success ? 'bg-green-50' : 'bg-red-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {r.success ? (
                                <CheckCircle2 size={16} className="text-green-600" />
                              ) : (
                                <AlertCircle size={16} className="text-red-600" />
                              )}
                              <span className="font-medium text-sm">{r.deviceName}</span>
                            </div>
                            <div className="text-xs text-slate-600">
                              {r.success
                                ? `${r.logsFound} / ${r.logsSaved}`
                                : r.error}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100">
              <button
                onClick={() => setSyncResult(null)}
                className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Employees Modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[92vh] flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Upload className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{t('devices.uploadTitle')}</h2>
                  <p className="text-sm text-slate-500">
                    {uploadModal.deviceName} - {uploadModal.ipAddress}
                    <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-xs">
                      {uploadModal.protocol || 'ZKTeco'}
                    </span>
                  </p>
                </div>
              </div>
              <button onClick={() => setUploadModal(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {!uploadResult ? (
                <>
                  {/* 2026-08-15: Santiye / Lokasyon / Departman — puantaj ekranindaki filtre seti */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                        <Building2 size={14} className="text-slate-400" /> Şantiye / Şube
                      </label>
                      <select
                        value={uploadForm.siteId ?? ''}
                        onChange={(e) => setUploadForm({ ...uploadForm, siteId: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Tümü</option>
                        {sites.map(x => (
                          <option key={x.id} value={x.id}>{x.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                        <MapPin size={14} className="text-slate-400" /> Lokasyon
                      </label>
                      <select
                        value={uploadForm.locationId ?? ''}
                        onChange={(e) => setUploadForm({ ...uploadForm, locationId: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Tümü</option>
                        {locations.map(x => (
                          <option key={x.id} value={x.id}>{x.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.departmentFilterLabel')}</label>
                      <select
                        value={uploadForm.departmentId || ''}
                        onChange={(e) => setUploadForm({ ...uploadForm, departmentId: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">{t('devices.allDepartmentsOption')}</option>
                        {departments.map(d => (
                          <option key={d.id} value={d.id}>{d.departmentName}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Personel listesi — tek tek veya tumunu sec */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={uploadSearch}
                          onChange={(e) => setUploadSearch(e.target.value)}
                          placeholder="Personel ara (ad, kod, kart no)..."
                          className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={tumUploadPersoneliSec}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 hover:bg-slate-100 whitespace-nowrap"
                      >
                        {filtrelenmisYuklemePersoneli.length > 0 &&
                         filtrelenmisYuklemePersoneli.every((e: any) => selectedUploadEmpIds.includes(e.id))
                          ? 'Seçimi Kaldır'
                          : 'Tümünü Seç'}
                      </button>
                      <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                        {selectedUploadEmpIds.length}/{filtrelenmisYuklemePersoneli.length}
                      </span>
                    </div>

                    <div className="max-h-56 overflow-y-auto">
                      {uploadEmpLoading ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                          <Loader2 size={22} className="animate-spin mx-auto mb-2" />
                          Personel yükleniyor...
                        </div>
                      ) : filtrelenmisYuklemePersoneli.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                          <Users size={22} className="mx-auto mb-2" />
                          Filtreye uyan personel yok
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <tbody className="divide-y divide-slate-100">
                            {filtrelenmisYuklemePersoneli.map((e: any) => (
                              <tr
                                key={e.id}
                                onClick={() => uploadPersoneliDegistir(e.id)}
                                className={`cursor-pointer hover:bg-amber-50 ${selectedUploadEmpIds.includes(e.id) ? 'bg-amber-50' : ''}`}
                              >
                                <td className="px-3 py-1.5 w-8">
                                  <input
                                    type="checkbox"
                                    checked={selectedUploadEmpIds.includes(e.id)}
                                    onChange={() => uploadPersoneliDegistir(e.id)}
                                    onClick={(ev) => ev.stopPropagation()}
                                    className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                  />
                                </td>
                                <td className="px-2 py-1.5 font-mono text-xs text-slate-500 w-20">{e.employeeCode || '-'}</td>
                                <td className="px-2 py-1.5 text-slate-800">{e.fullName}</td>
                                <td className="px-2 py-1.5 text-xs text-slate-400 text-right">{e.departmentName || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('devices.dateFilterLabel')}</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'all' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'all'
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterAll')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'today' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'today'
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterToday')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'week' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'week'
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterThisWeek')}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {uploadForm.dateFilter === 'today' && t('devices.dateFilterTodayDesc')}
                      {uploadForm.dateFilter === 'week' && t('devices.dateFilterWeekDesc')}
                      {uploadForm.dateFilter === 'all' && t('devices.dateFilterAllDesc')}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="includeInactive"
                      checked={uploadForm.includeInactive}
                      onChange={(e) => setUploadForm({ ...uploadForm, includeInactive: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="includeInactive" className="text-sm font-medium text-slate-700">
                      {t('devices.includeInactiveLabel')}
                    </label>
                  </div>

                  <div className={`flex items-center gap-3 p-4 rounded-xl border ${
                    uploadForm.dateFilter !== 'all'
                      ? 'bg-slate-100 border-slate-200 opacity-60'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <input
                      type="checkbox"
                      id="clearFirst"
                      checked={uploadForm.dateFilter === 'all' && uploadForm.clearFirst}
                      onChange={(e) => setUploadForm({ ...uploadForm, clearFirst: e.target.checked })}
                      disabled={uploadForm.dateFilter !== 'all'}
                      className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
                    />
                    <div>
                      <label htmlFor="clearFirst" className={`text-sm font-medium ${uploadForm.dateFilter !== 'all' ? 'text-slate-500' : 'text-red-700'}`}>
                        {t('devices.clearFirstLabel')}
                      </label>
                      {uploadForm.dateFilter !== 'all' && (
                        <p className="text-xs text-slate-500 mt-0.5">{t('devices.clearDisabledWhenFiltered')}</p>
                      )}
                    </div>
                  </div>

                  <div className={`rounded-xl p-4 border ${
                    uploadForm.dateFilter !== 'all'
                      ? 'bg-blue-50 border-blue-200'
                      : uploadForm.clearFirst
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-sm ${
                      uploadForm.dateFilter !== 'all'
                        ? 'text-blue-800'
                        : uploadForm.clearFirst
                          ? 'text-amber-800'
                          : 'text-green-800'
                    }`}>
                      <strong>{t('devices.infoLabel')}</strong> {uploadForm.dateFilter !== 'all'
                        ? (uploadForm.dateFilter === 'today' ? t('devices.infoFilteredToday') : t('devices.infoFilteredWeek'))
                        : uploadForm.clearFirst
                          ? t('devices.infoClearAndUpload')
                          : t('devices.infoUpdateAndAdd')}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  {uploading && uploadProgress.total > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">
                          {uploadProgress.percent === 0 ? t('devices.uploadingBatch') : t('devices.uploadingProgress')}
                        </span>
                        <span className="font-medium text-slate-800">
                          {uploadProgress.percent === 100
                            ? t('devices.uploadedCount', { count: uploadProgress.total })
                            : t('devices.employeeCount', { count: uploadProgress.total })}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                        {uploadProgress.percent === 0 ? (
                          <div className="h-3 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 animate-pulse rounded-full w-full" />
                        ) : (
                          <div
                            className="bg-gradient-to-r from-amber-500 to-amber-600 h-3 rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${uploadProgress.percent}%` }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">{t('devices.uploadResultTotal')}</p>
                      <p className="text-2xl font-black text-slate-800">{uploadResult.totalEmployees}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.uploadResultSuccessful')}</p>
                      <p className="text-2xl font-black text-green-600">{uploadResult.successCount}</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-red-600 uppercase font-bold mb-1">{t('devices.uploadResultFailed')}</p>
                      <p className="text-2xl font-black text-red-600">{uploadResult.failedCount}</p>
                    </div>
                  </div>

                  {uploadResult.users && uploadResult.users.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2">{t('devices.uploadedEmployeesFirst10')}</p>
                      <div className="bg-slate-50 rounded-xl p-3 max-h-40 overflow-y-auto">
                        {uploadResult.users.map((u, i) => (
                          <div key={i} className="flex items-center gap-2 py-1 text-sm">
                            <span className="font-mono text-slate-500">{u.enrollNumber}</span>
                            <span className="text-slate-700">{u.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3 flex-shrink-0 flex-wrap">
              <button
                onClick={() => setUploadModal(null)}
                className="py-3 px-5 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {uploadResult ? t('devices.closeButton') : t('devices.cancelButton')}
              </button>
              {!uploadResult && (
                <>
                  <button
                    onClick={handleClearDeviceOnly}
                    disabled={uploading || clearing}
                    className="py-3 px-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
                    title={t('devices.clearDevice')}
                  >
                    {clearing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    {t('devices.clearButton')}
                  </button>
                  <button
                    onClick={() => handleUploadEmployees('selected')}
                    disabled={uploading || clearing || selectedUploadEmpIds.length === 0}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
                    title="Yalnizca listede isaretledigin personelleri gonderir"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                    Seçilenleri Gönder ({selectedUploadEmpIds.length})
                  </button>
                  <button
                    onClick={() => handleUploadEmployees('filtered')}
                    disabled={uploading || clearing}
                    className="flex-1 py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 flex items-center justify-center gap-2 disabled:opacity-50"
                    title="Soldaki filtrelere uyan herkesi gonderir"
                  >
                    {uploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    {uploadForm.clearFirst ? t('devices.clearAndSendButton') : t('devices.sendButton')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal - Secili veya Tum Cihazlara */}
      {bulkUploadModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{t('devices.bulkUploadTitle')}</h2>
                  <p className="text-sm text-slate-500">
                    {selectedDeviceIds.length > 0
                      ? t('devices.bulkUploadDevicesSelected', { count: selectedDeviceIds.length })
                      : t('devices.bulkUploadActiveDevices', { count: devices.filter(d => d.isActive).length })}
                  </p>
                </div>
              </div>
              <button onClick={() => setBulkUploadModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {!uploadResult ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.departmentFilterLabel')}</label>
                    <select
                      value={uploadForm.departmentId || ''}
                      onChange={(e) => setUploadForm({ ...uploadForm, departmentId: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">{t('devices.allDepartmentsOption')}</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.departmentName}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('devices.dateFilterLabel')}</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'all' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'all'
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterAll')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'today' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'today'
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterToday')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadForm({ ...uploadForm, dateFilter: 'week' })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          uploadForm.dateFilter === 'week'
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t('devices.dateFilterThisWeek')}
                      </button>
                    </div>
                  </div>

                  <div className={`flex items-center gap-3 p-4 rounded-xl border ${
                    uploadForm.dateFilter !== 'all'
                      ? 'bg-slate-100 border-slate-200 opacity-60'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <input
                      type="checkbox"
                      id="bulkClearFirst"
                      checked={uploadForm.dateFilter === 'all' && uploadForm.clearFirst}
                      onChange={(e) => setUploadForm({ ...uploadForm, clearFirst: e.target.checked })}
                      disabled={uploadForm.dateFilter !== 'all'}
                      className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
                    />
                    <div>
                      <label htmlFor="bulkClearFirst" className={`text-sm font-medium ${uploadForm.dateFilter !== 'all' ? 'text-slate-500' : 'text-red-700'}`}>
                        {t('devices.bulkClearFirstLabel')}
                      </label>
                      {uploadForm.dateFilter !== 'all' && (
                        <p className="text-xs text-slate-500 mt-0.5">{t('devices.clearDisabledWhenFiltered')}</p>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {uploading && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">
                          {uploadProgress.percent === 0 ? t('devices.bulkUploadingBatch') : t('devices.bulkUploadingSending')}
                        </span>
                        <span className="font-medium text-slate-800">
                          {uploadProgress.total > 0 ? t('devices.bulkUploadDeviceCount', { count: uploadProgress.total }) : t('devices.bulkUploadPreparing')}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                        {uploadProgress.percent === 0 ? (
                          <div className="h-3 bg-gradient-to-r from-purple-400 via-purple-500 to-purple-400 animate-pulse rounded-full w-full" />
                        ) : (
                          <div
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${uploadProgress.percent}%` }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.uploadResultSuccessful')}</p>
                      <p className="text-2xl font-black text-green-600">{uploadResult.successCount}</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-red-600 uppercase font-bold mb-1">{t('devices.uploadResultFailed')}</p>
                      <p className="text-2xl font-black text-red-600">{uploadResult.failedCount}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => { setBulkUploadModal(false); setUploadResult(null); }}
                className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {uploadResult ? t('devices.closeButton') : t('devices.cancelButton')}
              </button>
              {!uploadResult && (
                <button
                  onClick={() => handleBulkUploadEmployees(selectedDeviceIds.length === 0)}
                  disabled={uploading}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Upload size={16} />
                  )}
                  {selectedDeviceIds.length > 0 ? t('devices.sendToDevicesButton', { count: selectedDeviceIds.length }) : t('devices.sendToAllDevicesButton')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Download Logs Modal */}
      {downloadModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Download className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{t('devices.downloadLogsTitle')}</h2>
                  <p className="text-sm text-slate-500">
                    {downloadModal.deviceName} - {downloadModal.ipAddress}
                    <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-xs">
                      {downloadModal.protocol || 'ZKTeco'}
                    </span>
                  </p>
                </div>
              </div>
              <button onClick={() => setDownloadModal(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {!downloadResult ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('devices.startDateLabel')}</label>
                    <input
                      type="date"
                      value={downloadForm.startDate}
                      onChange={(e) => setDownloadForm({ ...downloadForm, startDate: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">{t('devices.startDateDescription')}</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      <strong>{t('devices.infoLabel')}</strong> {t('devices.downloadInfoText')}
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">{t('devices.downloadRecordsFound')}</p>
                      <p className="text-2xl font-black text-slate-800">{downloadResult.count}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">{t('devices.downloadRecordsSaved')}</p>
                      <p className="text-2xl font-black text-green-600">{downloadResult.saved}</p>
                    </div>
                  </div>

                  {downloadResult.logs && downloadResult.logs.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2">{t('devices.downloadRecentRecords')}</p>
                      <div className="bg-slate-50 rounded-xl p-3 max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-slate-500 text-xs">
                              <th className="text-left py-1">{t('devices.downloadEmployee')}</th>
                              <th className="text-left py-1">{t('devices.downloadTime')}</th>
                              <th className="text-left py-1">{t('devices.downloadType')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {downloadResult.logs.slice(0, 10).map((log, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                <td className="py-1.5 font-mono">{log.employeeCode}</td>
                                <td className="py-1.5">{new Date(log.checkTime).toLocaleString()}</td>
                                <td className="py-1.5">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    log.checkType === 'CheckIn' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {log.checkType === 'CheckIn' ? t('devices.downloadCheckIn') : t('devices.downloadCheckOut')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setDownloadModal(null)}
                className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {downloadResult ? t('devices.closeButton') : t('devices.cancelButton')}
              </button>
              {!downloadResult && (
                <button
                  onClick={handleDownloadLogs}
                  disabled={downloading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {downloading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  {t('devices.downloadReadButton')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cihazdaki Personeller Onizleme Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <Users className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{t('devices.previewDeviceEmployees')}</h2>
                  <p className="text-sm text-slate-500">{previewModal.deviceName} ({previewModal.ipAddress})</p>
                </div>
              </div>
              <button onClick={() => { setPreviewModal(null); setEditingCard(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-purple-600" size={32} />
                  <span className="ml-3 text-slate-600">{t('devices.previewReadingUsers')}</span>
                </div>
              ) : previewUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t('devices.previewNoUsers')}</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm text-slate-600">
                      <span dangerouslySetInnerHTML={{ __html: t('devices.previewTotalEmployees', { count: previewUsers.length }) }} />
                      {selectedPreviewUsers.length > 0 && (
                        <span className="ml-2 text-red-600">{t('devices.previewSelectedCount', { count: selectedPreviewUsers.length })}</span>
                      )}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllPreviewUsers}
                        className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                      >
                        {t('devices.selectAll')}
                      </button>
                      <button
                        onClick={deselectAllPreviewUsers}
                        className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                      >
                        {t('devices.deselectAll')}
                      </button>
                      {selectedPreviewUsers.length > 0 && (
                        <button
                          onClick={handleDeleteSelectedUsers}
                          disabled={deletingUsers}
                          className="px-3 py-1 text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200 flex items-center gap-1 disabled:opacity-50"
                        >
                          {deletingUsers ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          {t('devices.deleteSelectedButton')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr className="text-slate-600 text-xs">
                          <th className="text-center px-2 py-3 w-8">
                            <input
                              type="checkbox"
                              checked={selectedPreviewUsers.length === previewUsers.length && previewUsers.length > 0}
                              onChange={e => e.target.checked ? selectAllPreviewUsers() : deselectAllPreviewUsers()}
                              className="rounded"
                            />
                          </th>
                          <th className="text-left px-3 py-3 font-bold">{t('devices.previewEmployeeCode')}</th>
                          <th className="text-left px-3 py-3 font-bold">{t('devices.previewFullName')}</th>
                          <th className="text-left px-3 py-3 font-bold">{t('devices.previewCardNo')}</th>
                          <th className="text-center px-2 py-3 font-bold w-16">{t('devices.previewAction')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {previewUsers.map((user, i) => (
                          <tr key={i} className={`hover:bg-white transition-colors ${selectedPreviewUsers.includes(user.enrollNumber) ? 'bg-red-50' : ''}`}>
                            <td className="text-center px-2 py-2.5">
                              <input
                                type="checkbox"
                                checked={selectedPreviewUsers.includes(user.enrollNumber)}
                                onChange={() => togglePreviewUserSelection(user.enrollNumber)}
                                className="rounded"
                              />
                            </td>
                            <td className="px-3 py-2.5 font-mono text-slate-700">{user.enrollNumber}</td>
                            <td className="px-3 py-2.5 text-slate-800">{user.name || '-'}</td>
                            <td className="px-3 py-2.5">
                              {editingCard?.enrollNumber === user.enrollNumber ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editingCard.cardNumber}
                                    onChange={e => setEditingCard({ ...editingCard, cardNumber: e.target.value })}
                                    className="w-28 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                                    placeholder={t('devices.previewCardPlaceholder')}
                                    autoFocus
                                  />
                                  <button
                                    onClick={handleUpdateUserCard}
                                    disabled={savingCard}
                                    className="p-1 text-green-600 hover:bg-green-100 rounded"
                                    title={t('common.save')}
                                  >
                                    {savingCard ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                  </button>
                                  <button
                                    onClick={() => setEditingCard(null)}
                                    className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                                    title={t('common.cancel')}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${user.cardNumber ? 'bg-blue-100 text-blue-700' : 'text-slate-400'}`}>
                                  {user.cardNumber || '-'}
                                </span>
                              )}
                            </td>
                            <td className="text-center px-2 py-2.5">
                              <button
                                onClick={() => setEditingCard({ enrollNumber: user.enrollNumber, cardNumber: user.cardNumber || '' })}
                                className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                title={t('devices.editCard')}
                              >
                                <Pencil size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`mx-6 mb-4 p-4 rounded-xl ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {importResult.message}
                </div>
                {importResult.success && importResult.importedUsers && importResult.importedUsers.length > 0 && (
                  <div className="mt-2 text-sm text-green-700">
                    <div className="font-medium mb-1">{t('devices.importedUsersLabel')}</div>
                    <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                      {importResult.importedUsers.map((user, i) => (
                        <li key={i}>{user}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 text-sm text-red-700">
                    <div className="font-medium mb-1">{t('devices.errorsLabel')}</div>
                    <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => { setPreviewModal(null); setEditingCard(null); setImportResult(null); }}
                className="py-3 px-6 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50"
              >
                {t('devices.closeButton')}
              </button>
              <button
                onClick={() => handlePreviewDeviceUsers(previewModal)}
                disabled={previewLoading || deletingUsers || importingUsers}
                className="py-3 px-6 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {previewLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
                {t('devices.refreshButton')}
              </button>
              <button
                onClick={handleImportUsersFromDevice}
                disabled={previewLoading || deletingUsers || importingUsers || previewUsers.length === 0}
                className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {importingUsers ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                {t('devices.importToDatabase')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hikvision Veri Çek — Tarih aralığı modal (Ag 2026) */}
      {hikPullModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md" onClick={() => setHikPullModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-lg font-bold text-slate-800">Hikvision Veri Çek</h2>
              <p className="text-xs text-slate-500 mt-1">{(hikPullModal.device as any).name} · {(hikPullModal.device as any).ipAddress}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Başlangıç</label>
                <input type="date" value={hikPullModal.startDate}
                  onChange={(e) => setHikPullModal({ ...hikPullModal, startDate: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Bitiş</label>
                <input type="date" value={hikPullModal.endDate}
                  onChange={(e) => setHikPullModal({ ...hikPullModal, endDate: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-mono" />
              </div>
              <div className="flex gap-2 text-xs">
                {[
                  { label: 'Bugün',      days: 0 },
                  { label: 'Son 7 gün',  days: 7 },
                  { label: 'Son 30 gün', days: 30 },
                ].map(preset => (
                  <button key={preset.label} onClick={() => {
                    const t = new Date();
                    const s = new Date(t.getTime() - preset.days * 86400000);
                    const fmt = (d: Date) => d.toISOString().split('T')[0];
                    setHikPullModal({ ...hikPullModal, startDate: fmt(s), endDate: fmt(t) });
                  }} className="flex-1 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold">{preset.label}</button>
                ))}
              </div>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-2">
              <button onClick={() => setHikPullModal(null)} className="flex-1 py-3 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 rounded-xl">İptal</button>
              <button onClick={executeHikvisionPull} className="flex-1 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-black text-xs uppercase tracking-widest flex items-center justify-center gap-1.5">
                <Download size={14} /> Çek
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
